import { useState } from "react";
import { Button, Tag, Tooltip, Typography } from "antd";
import { Hand, LogIn, MicOff, Radio, VideoOff, WifiOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { ExamPeriodMonitorState } from "@types";
import type { ProctoringCandidate } from "@hooks";
import { CandidateMediaControls } from "./CandidateMediaControls";
import { CandidateVideo } from "./CandidateVideo";
import type { ParticipantMedia } from "./useProctoringRoom";

export const candidateName = (c: ProctoringCandidate) =>
  `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || c.email || c.examInstanceId;

export const examStateTag = (state: number, t: TFunction) => {
  switch (state) {
    case ExamPeriodMonitorState.WaitingForParticipant:
      return <Tag color="gold">{t("pages.examPeriods.waitingForParticipant")}</Tag>;
    case ExamPeriodMonitorState.ParticipantConfirmed:
      return <Tag color="blue">{t("pages.examPeriods.participantConfirmed")}</Tag>;
    case ExamPeriodMonitorState.InProgress:
      return <Tag color="green">{t("pages.examPeriods.inProgress")}</Tag>;
    case ExamPeriodMonitorState.Paused:
      return <Tag color="orange">{t("pages.examPeriods.paused")}</Tag>;
    case ExamPeriodMonitorState.Excluded:
      return <Tag color="red">{t("pages.examPeriods.disqualified")}</Tag>;
    case ExamPeriodMonitorState.Completed:
      return <Tag>{t("pages.examPeriods.completed")}</Tag>;
    case ExamPeriodMonitorState.NoShow:
      return <Tag>{t("pages.examPeriods.notShowed")}</Tag>;
    default:
      return null;
  }
};

interface Props {
  examTermId: string;
  candidate: ProctoringCandidate;
  media?: ParticipantMedia;
  compact: boolean;
  showAssessor: boolean;
  talking?: boolean;
  onClick: () => void;
  onAction?: (text: string) => void;
  /** Shown while the candidate waits to be let into the exam. */
  onAdmit?: () => void;
  admitting?: boolean;
}

export const CandidateTile = ({ examTermId, candidate, media, compact, showAssessor, talking, onClick, onAction, onAdmit, admitting }: Props) => {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const name = candidateName(candidate);
  const connected = !!media?.connected;
  const cameraOff = connected && media?.cameraOn === false;
  const micOff = connected && media?.micOn === false;
  const help = connected && !!media?.help;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid="proctor-tile"
      data-identity={candidate.identity}
      data-connected={connected}
      data-camera={connected ? !cameraOff : undefined}
      data-mic={connected ? !micOff : undefined}
      data-help={help}
      style={{
        position: "relative",
        background: "#111",
        borderRadius: 6,
        overflow: "hidden",
        cursor: "pointer",
        minHeight: 0,
        outline: help ? "3px solid #fa8c16" : talking ? "3px solid #2251ff" : connected ? "2px solid #52c41a" : "2px solid #434343",
        animation: help ? "proctorHelpPulse 1.2s ease-in-out infinite" : undefined,
      }}
    >
      {media?.video && !cameraOff ? (
        <CandidateVideo track={media.video} />
      ) : (
        <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", color: "#8c8c8c", gap: 6 }}>
          {connected ? <VideoOff size={compact ? 16 : 24} /> : <WifiOff size={compact ? 16 : 24} />}
          {!compact && (
            <span>{!connected ? t("proctoring.notConnected") : cameraOff ? t("proctoring.cameraOff") : t("proctoring.noVideo")}</span>
          )}
        </div>
      )}
      {(hover || !compact) && connected && (
        <div style={{ position: "absolute", top: 6, right: 6, opacity: hover ? 1 : 0.85 }}>
          <CandidateMediaControls
            examTermId={examTermId}
            examInstanceId={candidate.examInstanceId}
            candidateName={name}
            media={media}
            onSent={onAction}
          />
        </div>
      )}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: compact ? "2px 6px" : "4px 8px",
          background: "linear-gradient(0deg, rgba(0,0,0,0.75) 0%, transparent 100%)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: "50%", flex: "none", background: connected ? "#52c41a" : "#8c8c8c" }} />
        {micOff && (
          <Tooltip title={t("proctoring.micOff")}>
            <MicOff size={compact ? 12 : 14} color="#ff7875" style={{ flex: "none" }} />
          </Tooltip>
        )}
        <Tooltip title={candidate.email}>
          <Typography.Text style={{ color: "#fff", fontSize: compact ? 11 : 13 }} ellipsis>
            {name}
          </Typography.Text>
        </Tooltip>
        {!compact && <span style={{ marginLeft: "auto" }}>{examStateTag(candidate.examInstanceState, t)}</span>}
        {onAdmit && candidate.examInstanceState === ExamPeriodMonitorState.WaitingForParticipant && (
          <Tooltip title={compact ? t("proctoring.admit") : undefined}>
            <Button
              size="small"
              type="primary"
              icon={<LogIn size={14} />}
              loading={admitting}
              style={{ flex: "none", marginLeft: compact ? "auto" : 0 }}
              onClick={(e) => {
                e.stopPropagation();
                onAdmit();
              }}
              data-testid="tile-admit"
            >
              {!compact && t("proctoring.admit")}
            </Button>
          </Tooltip>
        )}
      </div>
      {showAssessor && !compact && candidate.assessorName && (
        <Tag style={{ position: "absolute", top: 6, left: 6, margin: 0 }}>{candidate.assessorName}</Tag>
      )}
      {(help || talking) && (
        <div style={{ position: "absolute", top: compact ? 4 : 34, left: 6, display: "flex", gap: 4 }}>
          {help && (
            <Tag color="orange" icon={<Hand size={12} style={{ marginRight: 4 }} />} style={{ margin: 0 }} data-testid="proctor-help">
              {!compact && t("proctoring.helpRequested")}
            </Tag>
          )}
          {talking && (
            <Tag color="blue" icon={<Radio size={12} style={{ marginRight: 4 }} />} style={{ margin: 0 }}>
              {!compact && t("proctoring.youAreTalking")}
            </Tag>
          )}
        </div>
      )}
    </div>
  );
};
